<template>
  <div class="formulario-modelo">
    <header class="superior">
      <div class="header-flex">
        <h2 class="titulo-ficha">Ficha Documental</h2>
        <button class="btn salir" @click="idInstrumento != null ? emit('cerrar') : router.back()">
          {{ idInstrumento != null ? 'Cerrar' : 'Volver' }}
        </button>
      </div>
      <div v-if="nombreVersion || rutaComponente?.length || nombreIndicador" class="ctx-strip">
        <span v-if="nombreVersion" class="ctx-version">{{ nombreVersion }}</span>
        <template v-if="rutaComponente?.length">
          <div v-for="(nodo, i) in rutaComponente" :key="i" class="ctx-ruta-nodo">
            <span v-if="nodo.tipo" class="ctx-ruta-tipo">{{ nodo.tipo }}</span>
            <span v-if="nodo.ordinal" class="ctx-ruta-ordinal">{{ nodo.ordinal }}</span>
            <span class="ctx-ruta-nombre">{{ nodo.nombre }}</span>
          </div>
        </template>
        <div v-if="nombreIndicador" class="ctx-ruta-nodo ctx-indicador-row">
          <span class="ctx-ruta-tipo">Indicador</span>
          <span class="ctx-indicador">{{ nombreIndicador }}</span>
        </div>
      </div>
      <div class="divider"></div>
    </header>

    <!-- Tabla de fichas existentes -->
    <div class="contenedor-tabla">
      <div v-if="loading" class="info">Cargando fichas...</div>
      <table class="lista-tabla" v-else-if="fichasUnicas.length">
        <thead>
          <tr>
            <th>NO.</th>
            <th>ESTADO</th>
            <th>DOCUMENTO</th>
            <th>DETALLE</th>
            <th>ACCIÓN</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(f, i) in fichasUnicas" :key="f.id_ficha">
            <td>{{ i + 1 }}</td>
            <td>
              <span :class="['semaforo', estadoClass(f.estado)]">
                {{ estados[f.estado as keyof typeof estados] || f.estado }}
              </span>
            </td>
            <td class="doc-celda">
              <template v-if="renombrandoId === f.id_ficha">
                <div class="rename-row">
                  <input v-model="nuevoNombre" class="input-nombre" @keyup.enter="guardarNombre(f.id_ficha!)" @keyup.escape="cancelarRename" placeholder="Nombre del documento" />
                  <button class="btn guardar btn-sm-accion" @click="guardarNombre(f.id_ficha!)">OK</button>
                  <button class="btn salir btn-sm-accion" @click="cancelarRename">&#x2715;</button>
                </div>
              </template>
              <template v-else-if="f.origen_documento === 'cloudinary' || f.origen_documento === 'externo'">
                <a :href="f.documento" target="_blank" rel="noopener" class="link-doc">{{ f.nombre_visible || 'Ver documento' }}</a>
                <!-- Descarga aparte de «ver»: guarda el archivo con su nombre real. -->
                <a
                  v-if="f.url_descarga && f.origen_documento === 'cloudinary'"
                  :href="f.url_descarga"
                  class="link-descargar"
                  :title="`Descargar ${f.nombre_visible || 'archivo'}`"
                >&#11015;</a>
                <span v-if="f.origen_documento === 'externo'" class="badge-origen externo">externo</span>
                <span v-if="f.formato" class="badge-formato">{{ f.formato.toUpperCase() }}</span>
              </template>
              <template v-else-if="f.origen_documento === 'desconocido'">
                <span class="badge-origen desconocido">No disponible</span>
              </template>
              <template v-else-if="!f.origen_documento && f.documento && f.documento !== 'sin-documento' && f.documento !== 'pendiente-upload'">
                <a :href="f.documento" target="_blank" rel="noopener" class="link-doc">Ver documento</a>
              </template>
              <span v-else>-</span>
            </td>
            <td>{{ f.detalle }}</td>
            <td class="acciones-ficha">
              <button v-if="!fichasRecienCreadas.includes(f.id_ficha!)" class="btn guardar btn-sm-accion" @click="reutilizarFicha(f)">Reutilizar</button>
              <button
                v-if="f.id_ficha && renombrandoId !== f.id_ficha && f.documento && f.documento !== 'sin-documento' && f.documento !== 'pendiente-upload'"
                class="btn guardar btn-sm-accion"
                @click="iniciarRenombrar(f)"
              >Renombrar</button>
              <label class="btn guardar btn-upload" :class="{ disabled: subiendo === f.id_ficha }">
                {{ subiendo === f.id_ficha ? 'Subiendo...' : 'Subir archivo' }}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.txt"
                  hidden
                  :disabled="subiendo === f.id_ficha"
                  @change="subirArchivo(f.id_ficha!, $event)"
                />
              </label>
              <button class="btn salir" @click="abrirConfirmacionEliminar(f.id_ficha)">Eliminar</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="mensaje-vacio">No hay fichas registradas.</p>
    </div>

    <!-- Formulario nueva ficha -->
    <form class="ficha-form" @submit.prevent="guardarFicha">
      <h3>Nueva Ficha</h3>

      <!-- Estado (Semáforo) -->
      <div class="fila">
        <label>Estado del documento *</label>
        <div class="radio-group">
          <label v-for="key in ['existe', 'actualizable', 'no_existe']" :key="key" class="radio-label">
            <input type="radio" v-model="formData.estado" :value="key" required />
            <span :class="['indicador', `semaforo-${key}`]"></span>
            {{ estados[key as keyof typeof estados] }}
          </label>
        </div>
      </div>

      <!-- Subir archivo -->
      <div class="fila">
        <label>Subir archivo de evidencia</label>
        <div class="upload-area" :class="{ disabled: formData.estado === 'no_existe' }">
          <label class="upload-label" :class="{ disabled: formData.estado === 'no_existe' }">
            <input
              type="file"
              ref="fileInputRef"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.txt"
              hidden
              :disabled="formData.estado === 'no_existe'"
              @change="onFileSelected"
            />
            <span class="upload-icon">&#128206;</span>
            <span v-if="archivoSeleccionado">{{ archivoSeleccionado.name }}</span>
            <span v-else>Seleccionar archivo (PDF, DOC, XLS, IMG, etc.)</span>
          </label>
          <button
            v-if="archivoSeleccionado"
            type="button"
            class="btn-quitar"
            @click="quitarArchivo"
          >Quitar</button>
        </div>
      </div>

      <!-- Documento (enlace manual alternativo) -->
      <div class="fila">
        <label>O enlace del documento {{ !archivoSeleccionado && formData.estado !== 'no_existe' ? '*' : '' }}</label>
        <input
          type="url"
          v-model="formData.documento"
          :disabled="formData.estado === 'no_existe' || !!archivoSeleccionado"
          placeholder="https://..."
          class="form-control"
        />
        <small v-if="archivoSeleccionado" class="hint">El enlace se generará automáticamente al subir el archivo.</small>
      </div>

      <!-- Fuente de datos -->
      <div class="fila">
        <label>Fuente de datos {{ formData.estado !== 'no_existe' ? '*' : '' }}</label>
        <input 
          type="text" 
          v-model="formData.fuente_datos" 
          :disabled="formData.estado === 'no_existe'"
          placeholder="De dónde obtuvieron la información"
          class="form-control"
        />
      </div>

      <!-- Detalle -->
      <div class="fila">
        <label>Detalle de la ficha</label>
        <textarea 
          v-model="formData.detalle"
          :disabled="formData.estado === 'no_existe'"
          placeholder="Explicación del documento"
          class="form-control"
        ></textarea>
      </div>

      <!-- Observaciones -->
      <div class="fila">
        <label>Observaciones</label>
        <textarea 
          v-model="formData.observaciones"
          :disabled="formData.estado === 'no_existe'"
          placeholder="Notas adicionales"
          class="form-control"
        ></textarea>
      </div>

      <!-- Botones -->
      <div class="botones">
        <button type="button" class="btn salir" @click="idInstrumento != null ? emit('cerrar') : router.back()">
          {{ idInstrumento != null ? 'Cerrar' : 'Volver' }}
        </button>
        <button type="submit" class="btn guardar">Guardar Ficha</button>
      </div>
    </form>

    <!-- Componente de confirmación -->
    <MensajeConfirmacion
      :visible="showConfirmacion"
      :title="tituloConfirmacion"
      :message="mensajeConfirmacion"
      confirmText="Confirmar"
      cancelText="Cancelar"
      @confirm="confirmarAccion"
      @cancel="cancelarAccion"
    />

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Ficha de evidencia documental con su adjunto.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/ficha-documental` (FichaDocumental) — permiso `evidencias.subir`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { onMounted, ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGestionFichas } from '@/store/GestionFichas'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import type { Ficha } from '@/interfaces/Ficha'

interface RutaNodo { tipo: string; ordinal: string; nombre: string }

const props = withDefaults(defineProps<{
  idInstrumento?: number | null
  nombreVersion?: string
  rutaComponente?: RutaNodo[]
  nombreIndicador?: string
}>(), { idInstrumento: null })
const emit = defineEmits<{ (e: 'cerrar'): void }>()

const fichaStore = useGestionFichas()
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const subiendo = ref<number | null>(null)
const renombrandoId = ref<number | null>(null)
const fichasRecienCreadas = ref<number[]>([])
const nuevoNombre = ref('')

function iniciarRenombrar(f: Ficha) {
  renombrandoId.value = f.id_ficha!
  nuevoNombre.value = f.nombre_visible ?? f.nombre_documento ?? ''
}

function cancelarRename() {
  renombrandoId.value = null
  nuevoNombre.value = ''
}

async function guardarNombre(idFicha: number) {
  const nombre = nuevoNombre.value.trim()
  if (!nombre) { cancelarRename(); return }
  const resultado = await fichaStore.actualizarFicha(idFicha, { nombre_documento: nombre })
  if (resultado) {
    mostrarMensaje('Nombre actualizado', 'positivo')
    await cargarFichas()
  } else {
    mostrarMensaje('Error al actualizar el nombre', 'error')
  }
  cancelarRename()
}
const archivoSeleccionado = ref<File | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

const fichasLocales = ref<Ficha[]>([])

const fichasUnicas = computed(() => {
  const vistas = new Set<number>()
  return fichasLocales.value.filter(f => {
    if (!f.id_ficha || vistas.has(f.id_ficha)) return false
    vistas.add(f.id_ficha)
    return true
  })
})


const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const estados = {
  existe: '🟢 Existe',
  actualizable: '🟡 Actualizable',
  no_existe: '🔴 No existe'
}

const formData = ref<Omit<Ficha, 'id_ficha' | 'tipo'>>({
  id_instrumento: 0,
  estado: '',
  documento: '',
  fuente_datos: '',
  detalle: '',
  observaciones: '',
  descripcion: '',
  fecha_registro: new Date().toISOString().split('T')[0]
})

// Control de confirmación y toast
const showConfirmacion = ref(false)
const tituloConfirmacion = ref('')
const mensajeConfirmacion = ref('')
let accionPendiente: (() => Promise<void>) | null = null

function parseInstrumentoId(input: unknown): number | null {
  if (input === null || input === undefined) return null
  const raw = Array.isArray(input) ? input[0] : input
  const value = typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : Number(String(raw))
  return Number.isFinite(value) && value > 0 ? value : null
}

async function cargarFichas() {
  const id = parseInstrumentoId(
    props.idInstrumento ??
    (route.query as any).id_instrumento ??
    (route.params as any).id_instrumento ??
    (route.params as any).id
  )
  if (!id) {
    mostrarMensaje('No se encontró id_instrumento en la URL.', 'error')
    return
  }

  formData.value.id_instrumento = id
  loading.value = true
  try {
    fichasLocales.value = (await fichaStore.listarFichas({ id_instrumento: id, tipo: 'documental' })) ?? []
  } finally {
    loading.value = false
  }
}

onMounted(cargarFichas)

watch(() => props.idInstrumento, (val) => {
  if (val != null) cargarFichas()
})

function estadoClass(estado: string | undefined) {
  return `semaforo-${estado}`
}

async function guardarFicha() {
  if (!formData.value.id_instrumento || formData.value.id_instrumento <= 0) {
    mostrarMensaje('id_instrumento es requerido para crear la ficha.', 'error')
    return
  }

  // Validar que estado esté seleccionado
  if (!formData.value.estado) {
    mostrarMensaje('Por favor seleccione un estado', 'error')
    return
  }

  // Validar campos requeridos según estado
  if (formData.value.estado !== 'no_existe') {
    if (!archivoSeleccionado.value && !formData.value.documento?.trim()) {
      mostrarMensaje('Suba un archivo o ingrese un enlace del documento', 'error')
      return
    }
    if (!formData.value.fuente_datos?.trim()) {
      mostrarMensaje('La fuente de datos es requerida', 'error')
      return
    }
  }

  // Preparar confirmación
  tituloConfirmacion.value = 'Guardar Ficha Documental'
  mensajeConfirmacion.value = '¿Está seguro de que desea guardar esta ficha?'
  
  accionPendiente = async () => {
    formData.value.descripcion = formData.value.detalle?.trim() || formData.value.estado || 'N/A'
    if (!formData.value.fecha_registro) {
      formData.value.fecha_registro = new Date().toISOString().split('T')[0]
    }

    // Si hay archivo seleccionado, usar placeholder para documento
    const docValue = archivoSeleccionado.value
      ? 'pendiente-upload'
      : (formData.value.documento?.trim() || 'sin-documento')

    const payload: Omit<Ficha, 'id_ficha'> = {
      ...formData.value,
      documento: docValue,
      tipo: 'documental'
    }

    const resultado = await fichaStore.crearFicha(payload)

    if (resultado.ok && resultado.ficha) {
      if (resultado.ficha.id_ficha) fichasRecienCreadas.value.push(resultado.ficha.id_ficha)
      // Si hay archivo, subirlo a Cloudinary
      if (archivoSeleccionado.value && resultado.ficha.id_ficha) {
        mostrarMensaje('Ficha creada. Subiendo archivo...', 'positivo')
        const uploadResult = await fichaStore.uploadArchivo(resultado.ficha.id_ficha, archivoSeleccionado.value)
        if (uploadResult.ok) {
          mostrarMensaje('Ficha guardada y archivo subido exitosamente', 'positivo')
        } else {
          mostrarMensaje(uploadResult.error || 'Ficha creada pero error al subir archivo', 'error')
        }
      } else {
        mostrarMensaje('Ficha documental guardada exitosamente', 'positivo')
      }
      archivoSeleccionado.value = null
      limpiarFormulario()
      await cargarFichas()
    } else {
      mostrarMensaje(resultado.error || 'Error al guardar la ficha', 'error')
    }
  }

  showConfirmacion.value = true
}

function limpiarFormulario() {
  formData.value = {
    id_instrumento: 0,
    estado: '',
    documento: '',
    fuente_datos: '',
    detalle: '',
    observaciones: '',
    descripcion: '',
    fecha_registro: new Date().toISOString().split('T')[0]
  }
}

function abrirConfirmacionEliminar(id: number | undefined) {
  if (!id) return

  tituloConfirmacion.value = 'Eliminar Ficha'
  mensajeConfirmacion.value = '¿Está seguro de que desea eliminar esta ficha? Esta acción no se puede deshacer.'
  
  accionPendiente = async () => {
    const resultado = await fichaStore.eliminarFicha(id)
    
    if (resultado) {
      mostrarMensaje('Ficha eliminada exitosamente', 'positivo')
      await cargarFichas()
    } else {
      mostrarMensaje('Error al eliminar la ficha', 'error')
    }
  }

  showConfirmacion.value = true
}

function confirmarAccion() {
  showConfirmacion.value = false
  if (accionPendiente) {
    accionPendiente()
    accionPendiente = null
  }
}

function cancelarAccion() {
  showConfirmacion.value = false
  accionPendiente = null
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    archivoSeleccionado.value = file
  }
}

function quitarArchivo() {
  archivoSeleccionado.value = null
  if (fileInputRef.value) fileInputRef.value.value = ''
}

function reutilizarFicha(f: Ficha) {
  archivoSeleccionado.value = null
  if (fileInputRef.value) fileInputRef.value.value = ''
  formData.value = {
    id_instrumento: formData.value.id_instrumento,
    estado: f.estado ?? '',
    documento: (f.documento && f.documento !== 'sin-documento' && f.documento !== 'pendiente-upload')
      ? f.documento
      : '',
    fuente_datos: f.fuente_datos ?? '',
    detalle: f.detalle ?? '',
    observaciones: f.observaciones ?? '',
    descripcion: f.descripcion ?? '',
    fecha_registro: new Date().toISOString().split('T')[0]
  }
  document.querySelector<HTMLElement>('.ficha-form')?.scrollIntoView({ behavior: 'smooth' })
}

async function subirArchivo(idFicha: number, event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !idFicha) return

  subiendo.value = idFicha
  try {
    const result = await fichaStore.uploadArchivo(idFicha, file)
    if (result.ok) {
      mostrarMensaje('Archivo subido exitosamente', 'positivo')
    } else {
      mostrarMensaje(result.error || 'Error al subir archivo', 'error')
    }
    await cargarFichas()
  } finally {
    subiendo.value = null
    input.value = ''
  }
}
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  width: 95%;
  height: 100%;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  margin: 20px auto;
  font-family: var(--font-family);
  max-width: 900px;
}

.titulo-ficha {
  margin-bottom: 0.5rem;
  font-size: 1.4rem;
}

.ctx-strip {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 12px;
  margin: 0.5rem 0;
  background: #e8f0f8;
  border-left: 3px solid #0e69a0;
  border-radius: 3px;
}
.ctx-version {
  font-size: 0.8rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
}
.ctx-ruta-nodo {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.ctx-ruta-tipo {
  font-size: 0.7rem;
  font-weight: 700;
  color: #0e69a0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  min-width: 88px;
  flex-shrink: 0;
}
.ctx-ruta-ordinal {
  font-size: 0.78rem;
  font-weight: 700;
  color: #555;
  min-width: 28px;
  flex-shrink: 0;
}
.ctx-ruta-nombre {
  font-size: 0.78rem;
  color: #333;
}
.ctx-indicador-row {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #c5d8ec;
}
.ctx-indicador {
  font-size: 0.8rem;
  font-weight: 600;
  color: #111;
}

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.divider {
  border: none;
  border-bottom: 1px solid #949393;
  margin: 8px 0 16px;
}

.contenedor-tabla {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
}

.info {
  padding: 0.5rem 0;
  color: #333;
}

.mensaje-vacio {
  margin-top: 0.5rem;
  color: #555;
}

.lista-tabla {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  table-layout: auto;
}

.lista-tabla td,
.lista-tabla th {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  vertical-align: middle;
}

.lista-tabla thead {
  background-color: #e2e2e2;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
}

.semaforo {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.semaforo-existe {
  background-color: #d4edda;
  color: #155724;
}

.semaforo-actualizable {
  background-color: #fff3cd;
  color: #856404;
}

.semaforo-no_existe {
  background-color: #f8d7da;
  color: #721c24;
}

.ficha-form {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
}

.ficha-form h3 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.fila {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.fila > label {
  font-weight: 600;
  color: #333;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: normal;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

.indicador {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.indicador.semaforo-existe { background-color: #28a745; box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.2); }
.indicador.semaforo-actualizable { background-color: #ffc107; box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.2); }
.indicador.semaforo-no_existe { background-color: #dc3545; box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2); animation: pulse 2s infinite; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.form-control {
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #ececec;
  font-size: 1rem;
  font-family: inherit;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-control:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.form-control:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

textarea.form-control {
  min-height: 80px;
  resize: vertical;
}

.botones {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
}

.btn {
  padding: 0.5rem 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  line-height: 1.2;
  box-sizing: border-box;
  transition: all var(--transition-fast);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
}

.btn.salir:hover {
  background-color: rgb(212, 26, 26);
  color: white;
  border-color: rgb(212, 26, 26);
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

.btn.guardar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}

.acciones-ficha {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.btn-sm-accion {
  font-size: 0.8rem;
  padding: 0.35rem 0.7rem;
  height: auto;
}

.btn-upload {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  padding: 0.5rem 0.9rem;
}

.btn-upload.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.link-doc {
  color: #0e69a0;
  text-decoration: underline;
  font-weight: 500;
}

.link-doc:hover {
  color: #094d75;
}

.upload-area {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.upload-area.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.upload-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background-color: #f0f4f8;
  border: 2px dashed #b0c4d8;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  transition: all 0.15s ease;
  flex: 1;
  min-width: 200px;
}

.upload-label:hover {
  background-color: #e1ecf4;
  border-color: #0e69a0;
}

.upload-label.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.upload-icon {
  font-size: 1.2rem;
}

.btn-quitar {
  padding: 0.4rem 0.75rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.15s ease;
}

.btn-quitar:hover {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.hint {
  color: #888;
  font-size: 0.8rem;
  font-style: italic;
}

.aviso-historico {
  background: #e8f1f8;
  border: 1px solid #b0cfe8;
  border-left: 4px solid #0e69a0;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
}

.aviso-historico-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.35rem;
}

.aviso-historico-titulo {
  font-size: 0.88rem;
  font-weight: 700;
  color: #0e69a0;
}

.aviso-cerrar {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: #666;
  padding: 0;
  line-height: 1;
}

.aviso-cerrar:hover { color: #333; }

.aviso-historico-desc {
  font-size: 0.83rem;
  color: #444;
  margin: 0 0 0.6rem;
}

.aviso-historico-items {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.aviso-historico-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: white;
  border: 1px solid #d0e5f5;
  border-radius: 4px;
  padding: 0.4rem 0.75rem;
}

.aviso-historico-info {
  font-size: 0.83rem;
  color: #333;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aviso-historico-acciones {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-ver {
  font-size: 0.78rem;
  padding: 0.28rem 0.6rem;
  background: #f0f4f8;
  border: 1px solid #b0c4d8;
  border-radius: var(--radius-md);
  color: #0e69a0;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s ease;
}

.btn-ver:hover {
  background: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.doc-celda { min-width: 160px; }

.rename-row {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: nowrap;
}

.input-nombre {
  flex: 1;
  padding: 3px 6px;
  border: 1px solid #0e69a0;
  border-radius: 4px;
  font-size: 0.82rem;
  min-width: 0;
  font-family: inherit;
}

.badge-origen {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: 4px;
  white-space: nowrap;
  vertical-align: middle;
}

.badge-origen.externo {
  background: #fdf3e1;
  color: #a07010;
  border: 1px solid #f5c96a;
}

.badge-origen.desconocido {
  background: #fde8e8;
  color: #a32d2d;
  border: 1px solid #f5c6c6;
}

.badge-formato {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  background: #e8f2f9;
  color: #0e5480;
  border: 1px solid #b8d8ee;
  margin-left: 4px;
  vertical-align: middle;
}
.link-descargar {
  display: inline-block;
  margin-left: 0.4rem;
  color: #0e69a0;
  text-decoration: none;
  font-size: 0.85rem;
  opacity: 0.75;
}
.link-descargar:hover { opacity: 1; }

</style>